import React from 'react';
import { Link } from 'react-router-dom';
import CompanyDetailsBlock from '../components/CompanyDetailsBlock';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h1
          className="text-4xl sm:text-5xl tracking-tight leading-none font-bold text-[#112318] mb-4"
          style={{ fontFamily: 'Playfair Display, serif' }}
          data-testid="privacy-title"
        >
          Privacy Policy
        </h1>
        <p className="text-sm text-[#4A5D50] mb-12">Effective Date: 14 April 2026</p>

        <div className="prose prose-sm max-w-none space-y-10 text-[#4A5D50]">

          <section>
            <h2 className="text-xl font-semibold text-[#112318] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              1. Introduction
            </h2>
            <p className="leading-relaxed">
              VinePass ("we", "our", "us") is committed to protecting your personal information and your
              right to privacy. This Privacy Policy explains how we collect, use, store, and share your
              information when you use our website
              (<a href="https://vinepass.co.za" className="text-[#1F3D2B] font-semibold hover:underline">vinepass.co.za</a>)
              and services.
            </p>
            <p className="leading-relaxed mt-3">
              This policy is in compliance with the Protection of Personal Information Act (POPIA) of
              South Africa. By using VinePass, you consent to the practices described in this policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#112318] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              2. Information We Collect
            </h2>
            <p className="leading-relaxed mb-3">We may collect the following types of personal information:</p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li><strong>Account Information:</strong> Name, email address, and password when you create an account.</li>
              <li><strong>Purchase Information:</strong> Name, email, and ticket details when you purchase tickets.</li>
              <li><strong>Event Organiser Information:</strong> Name, email, event details, and payment information for payouts.</li>
              <li><strong>Usage Data:</strong> Browser type, IP address, pages visited, and access times collected automatically.</li>
              <li><strong>Cookies:</strong> Authentication tokens stored in secure, HTTP-only cookies to maintain your session.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#112318] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              3. How We Use Your Information
            </h2>
            <p className="leading-relaxed mb-3">We use your personal information to:</p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>Process ticket purchases and send confirmation emails.</li>
              <li>Manage your account and provide customer support.</li>
              <li>Communicate with you about events, updates, and promotions (with your consent).</li>
              <li>Enable event organisers to manage their events and track sales.</li>
              <li>Improve our website, services, and user experience.</li>
              <li>Comply with legal obligations and prevent fraud.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#112318] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              4. How We Share Your Information
            </h2>
            <p className="leading-relaxed mb-3">
              We do not sell your personal information. We may share your information with:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li><strong>Event Organisers:</strong> Your name and email may be shared with the organiser of events you purchase tickets for, solely for event management purposes.</li>
              <li><strong>Payment Providers:</strong> PayFast and Stripe process your payment securely. We do not store full payment details.</li>
              <li><strong>Email Service Providers:</strong> We use third-party email services to send you ticket confirmations and communications.</li>
              <li><strong>Law Enforcement:</strong> We may disclose information if required by law or in response to valid legal requests.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#112318] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              5. Data Security
            </h2>
            <p className="leading-relaxed">
              We implement appropriate technical and organisational measures to protect your personal
              information, including:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-2 mt-3">
              <li>Encrypted password storage using industry-standard hashing algorithms.</li>
              <li>Secure, HTTP-only cookies for authentication.</li>
              <li>HTTPS encryption for all data transmission.</li>
              <li>Rate limiting to prevent abuse and unauthorised access attempts.</li>
              <li>Input validation and sanitisation to prevent injection attacks.</li>
              <li>Regular security monitoring and updates.</li>
            </ul>
            <p className="leading-relaxed mt-3">
              While we strive to protect your data, no method of electronic transmission or storage is
              100% secure. We cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#112318] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              6. Data Retention
            </h2>
            <p className="leading-relaxed">
              We retain your personal information for as long as your account is active or as needed to
              provide services. We may retain certain information as required by law, for legitimate
              business purposes, or to resolve disputes. You may request deletion of your account and
              associated data at any time by contacting us.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#112318] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              7. Your Rights Under POPIA
            </h2>
            <p className="leading-relaxed mb-3">
              Under the Protection of Personal Information Act (POPIA), you have the right to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>Access your personal information held by us.</li>
              <li>Request correction of inaccurate or incomplete information.</li>
              <li>Request deletion of your personal information.</li>
              <li>Object to the processing of your personal information.</li>
              <li>Withdraw consent for marketing communications at any time.</li>
              <li>Lodge a complaint with the Information Regulator of South Africa.</li>
            </ul>
            <p className="leading-relaxed mt-3">
              To exercise any of these rights, please contact us at{' '}
              <a href="mailto:info@vinepass.co.za" className="text-[#1F3D2B] font-semibold hover:underline">
                info@vinepass.co.za
              </a>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#112318] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              8. Cookies
            </h2>
            <p className="leading-relaxed">
              VinePass uses essential cookies to maintain your authentication session. These cookies
              are necessary for the platform to function and cannot be opted out of. We do not use
              advertising or tracking cookies. Your cookie preferences are respected, and no
              third-party cookies are placed on your device through our platform.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#112318] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              9. Third-Party Links
            </h2>
            <p className="leading-relaxed">
              Our website may contain links to third-party websites or services, such as payment
              providers and event organiser websites. We are not responsible for the privacy practices
              or content of these external sites. We encourage you to review their privacy policies
              before providing any personal information.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#112318] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              10. Children's Privacy
            </h2>
            <p className="leading-relaxed">
              VinePass is not intended for use by individuals under the age of 18. We do not knowingly
              collect personal information from children. If we become aware that a child has provided
              us with personal information, we will take steps to delete it promptly.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#112318] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              11. Changes to This Policy
            </h2>
            <p className="leading-relaxed">
              We may update this Privacy Policy from time to time. Any changes will be posted on
              this page with a revised effective date. We encourage you to review this policy
              periodically. Continued use of VinePass after any changes constitutes your acceptance
              of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#112318] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              12. Contact Us
            </h2>
            <p className="leading-relaxed">
              If you have any questions, concerns, or requests regarding this Privacy Policy or
              how we handle your personal information, please contact us:
            </p>
            <div className="mt-4 space-y-2">
              <p>
                <span className="text-[#112318] font-medium">Email: </span>
                <a href="mailto:info@vinepass.co.za" className="text-[#1F3D2B] font-semibold hover:underline" data-testid="privacy-contact-email">
                  info@vinepass.co.za
                </a>
              </p>
              <p>
                <span className="text-[#112318] font-medium">Website: </span>
                <a href="https://vinepass.co.za" className="text-[#1F3D2B] font-semibold hover:underline">
                  vinepass.co.za
                </a>
              </p>
            </div>
          </section>

          <section className="bg-[#F4F6F3] rounded-xl p-6 border border-[#E5E9E6]">
            <p className="text-sm text-[#4A5D50] leading-relaxed">
              By using VinePass, you acknowledge that you have read and understood this Privacy Policy
              and agree to the collection and use of your information as described herein.
            </p>
          </section>

          <CompanyDetailsBlock />

        </div>

        <div className="mt-16 pt-8 border-t border-[#E5E9E6] flex items-center justify-between">
          <Link to="/" className="text-[#1F3D2B] font-semibold hover:underline text-sm" data-testid="privacy-back-home">
            &larr; Back to Home
          </Link>
          <Link to="/terms" className="text-[#1F3D2B] font-semibold hover:underline text-sm" data-testid="privacy-to-terms">
            Terms & Conditions &rarr;
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
